/**
 * SAMWISE — Voice / TTS Client
 *
 * Speaks using Azure Neural TTS via Supabase edge function,
 * with Web Speech API as offline fallback.
 *
 * Privacy: audio is synthesized remotely from text. No learner
 * data is sent — only Sam's own scripted lines.
 */

import { SAMWISE_VOICE } from './samwise-persona';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Supabase project URL — set via environment or fall back to production */
const SUPABASE_URL =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) ||
  'https://tkmfpuowjdzriddbskak.supabase.co';

const SUPABASE_ANON_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_ANON_KEY) ||
  '';

// ---------------------------------------------------------------------------
// TTS Client
// ---------------------------------------------------------------------------

export class SamwiseVoice {
  private audioCtx: AudioContext | null = null;
  private speaking = false;
  private queue: Array<{ text: string; resolve: () => void }> = [];
  private currentAudio: HTMLAudioElement | null = null;
  private useAzure: boolean;
  private volume = 0.85;
  private onSpeakingChange?: (speaking: boolean) => void;

  constructor(opts?: { onSpeakingChange?: (speaking: boolean) => void }) {
    // Use Azure TTS if we have the Supabase URL (not on localhost without config)
    this.useAzure = !!SUPABASE_URL && SUPABASE_URL.includes('supabase.co');
    this.onSpeakingChange = opts?.onSpeakingChange;
  }

  /**
   * Speak a line. Returns a promise that resolves when speech finishes.
   * Queues if already speaking.
   */
  speak(text: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.queue.push({ text, resolve });
      if (!this.speaking) {
        this.processQueue();
      }
    });
  }

  /**
   * Stop all speech immediately.
   */
  stop(): void {
    this.queue = [];
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    this.setSpeaking(false);
  }

  /**
   * Set the volume (0–1).
   */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.currentAudio) {
      this.currentAudio.volume = this.volume;
    }
  }

  /**
   * Whether Sam is currently speaking.
   */
  isSpeaking(): boolean {
    return this.speaking;
  }

  // -------------------------------------------------------------------------
  // Private
  // -------------------------------------------------------------------------

  private setSpeaking(val: boolean): void {
    this.speaking = val;
    this.onSpeakingChange?.(val);
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      this.setSpeaking(false);
      return;
    }

    this.setSpeaking(true);
    const { text, resolve } = this.queue.shift()!;

    try {
      if (this.useAzure) {
        await this.speakAzure(text);
      } else {
        await this.speakWebSpeech(text);
      }
    } catch (err) {
      console.warn('SAMWISE TTS: Azure failed, falling back to Web Speech', err);
      try {
        await this.speakWebSpeech(text);
      } catch {
        // Silently fail — Sam will show text but not speak
        console.warn('SAMWISE TTS: Web Speech also failed');
      }
    }

    resolve();
    // Small pause between utterances
    await this.delay(300);
    this.processQueue();
  }

  /**
   * Speak via Azure TTS (Supabase edge function).
   */
  private async speakAzure(text: string): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (SUPABASE_ANON_KEY) {
      headers['apikey'] = SUPABASE_ANON_KEY;
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/tts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        voice: SAMWISE_VOICE.voiceId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Azure TTS error: ${response.status}`);
    }

    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);

    return new Promise<void>((resolve, reject) => {
      const audio = new Audio(audioUrl);
      audio.volume = this.volume;
      this.currentAudio = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        resolve();
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        reject(new Error('Audio playback failed'));
      };

      audio.play().catch(reject);
    });
  }

  /**
   * Speak via Web Speech API (browser built-in, offline fallback).
   */
  private speakWebSpeech(text: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!window.speechSynthesis) {
        reject(new Error('Web Speech API not available'));
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = SAMWISE_VOICE.language;
      utterance.rate = SAMWISE_VOICE.rate;
      utterance.pitch = 1.0 + (SAMWISE_VOICE.pitch / 50); // normalize to 0–2 range
      utterance.volume = this.volume;

      // Try to find a British English voice
      const voices = window.speechSynthesis.getVoices();
      const britishVoice = voices.find(v =>
        v.lang.startsWith('en-GB') && v.name.toLowerCase().includes('male')
      ) || voices.find(v =>
        v.lang.startsWith('en-GB')
      ) || voices.find(v =>
        v.lang.startsWith('en')
      );

      if (britishVoice) {
        utterance.voice = britishVoice;
      }

      utterance.onend = () => resolve();
      utterance.onerror = (e) => reject(e);

      window.speechSynthesis.speak(utterance);
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}
